import instance from '@/config/axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ChannelOwnership,
  GetChannelSettingsResponse,
  PostChannelAudienceResponse,
  PostChannelOwnershipRequest,
  PostChannelOwnershipResponse,
  PostChannelOwnershipVerifyRequest,
  PutChannelSettingsRequest,
  PutChannelSettingsResponse,
} from './type'
import { useCollableMutation } from '@/config/useCollableMutation'
import type { ChannelId } from '@/types/common'

export const apiGetChannelOwnerships = async () => {
  const { data } = await instance.get<ChannelOwnership[]>('/v1/channel-ownerships')
  return data
}

export const useGetChannelOwnerships = (enabled = true) => {
  return useQuery({
    queryKey: ['channel-ownerships'],
    queryFn: apiGetChannelOwnerships,
    enabled,
  })
}

export const apiPostChannelOwnership = async (payload: PostChannelOwnershipRequest) => {
  const { data } = await instance.post<PostChannelOwnershipResponse>(
    '/v1/channel-ownerships',
    payload,
  )
  return data
}

export const usePostChannelOwnership = () => {
  const queryClient = useQueryClient()
  return useCollableMutation({
    mutationFn: apiPostChannelOwnership,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-ownerships'] }),
  })
}

export const apiPostChannelOwnershipVerify = async (payload: PostChannelOwnershipVerifyRequest) => {
  const { data } = await instance.post(
    `/v1/channel-ownerships/${payload.ownershipRequestId}/verify`,
    {
      otp: payload.otp,
    },
  )
  return data
}

export const usePostChannelOwnershipVerify = () => {
  const queryClient = useQueryClient()
  return useCollableMutation({
    mutationFn: apiPostChannelOwnershipVerify,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel-ownerships'] }),
  })
}

export const apiGetChannelSettings = async (channelId: ChannelId) => {
  const { data } = await instance.get<GetChannelSettingsResponse>(
    `/v1/channels/${channelId}/settings`,
  )
  return data
}

export const useGetChannelSettings = (channelId: ChannelId) => {
  return useQuery({
    queryKey: ['channel-settings', channelId],
    queryFn: () => apiGetChannelSettings(channelId),
    enabled: !!channelId,
  })
}

export const apiPutChannelSettings = async ({
  channelId,
  payload,
}: {
  channelId: ChannelId
  payload: PutChannelSettingsRequest
}) => {
  const { data } = await instance.put<PutChannelSettingsResponse>(
    `/v1/channels/${channelId}/settings`,
    payload,
  )
  return data
}

export const usePutChannelSettings = () => {
  const queryClient = useQueryClient()
  return useCollableMutation({
    mutationFn: apiPutChannelSettings,
    onSuccess: res =>
      queryClient.invalidateQueries({ queryKey: ['channel-settings', res.channelId] }),
  })
}

export const apiPostChannelAudience = async ({
  channelId,
  file,
}: {
  channelId: ChannelId
  file: File
}) => {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await instance.post<PostChannelAudienceResponse>(
    `/v1/channels/${channelId}/settings/audience`,
    fd,
  )
  return data
}

export const usePostChannelAudience = () => {
  return useCollableMutation({
    mutationFn: apiPostChannelAudience,
  })
}
